import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

// Lazily loads the Google Maps JS API + Places library exactly once per page.
// Subsequent callers get the same in-flight or resolved promise.

let loaderPromise: Promise<void> | null = null;

export function loadGoogleMaps(): Promise<void> {
  if (loaderPromise) return loaderPromise;

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    return Promise.reject(
      new Error(
        'Missing REACT_APP_GOOGLE_MAPS_API_KEY. Add it to .env.local to use the restaurant search.'
      )
    );
  }

  setOptions({ key: apiKey, v: 'weekly', libraries: ['places'] });

  loaderPromise = importLibrary('places')
    .then(() => undefined)
    .catch((err) => {
      // Reset so a later retry re-attempts the load.
      loaderPromise = null;
      throw err;
    });

  return loaderPromise;
}

// Maps Google's `types` array to a human cuisine label. Picks the first
// `*_restaurant` variant when present, else falls back to a small set of
// common venue types, else "Restaurant".
export function placeTypeToCuisine(types: ReadonlyArray<string> | undefined): string {
  if (!types || types.length === 0) return 'Restaurant';

  for (const t of types) {
    const match = t.match(/^([a-z_]+)_restaurant$/);
    if (match) {
      return match[1]
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
    }
  }

  if (types.includes('cafe')) return 'Café';
  if (types.includes('bakery')) return 'Bakery';
  // Before `bar`: breweries are usually tagged with both, and "Brewery"
  // is the more specific label.
  if (types.includes('brewery')) return 'Brewery';
  if (types.includes('bar')) return 'Bar';
  if (types.includes('meal_takeaway')) return 'Takeout';
  return 'Restaurant';
}

